import React, { useState, useEffect, useMemo } from 'react';
import { userService, UserManagement as UserType, CreateUserData, UpdateUserData } from '@/services/userService';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Edit, Save, X, AlertCircle, User } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface UserManagementProps {
  searchTerm: string;
  statusFilter: 'all' | 'active' | 'inactive';
}

const UserManagement: React.FC<UserManagementProps> = ({ searchTerm, statusFilter }) => {
  const { canManageUsers } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [saving, setSaving] = useState(false);
  const [allStaff, setAllStaff] = useState<{ id: string; name: string; role: string; status: string; user_id: string | null }[]>([]);
  
  // Form state
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    name: '',
    password: '',
    role: 'office_user',
    allowed_clinics: [],
    staff_id: undefined
  });

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full system access' },
    { value: 'operations_manager', label: 'Operations Manager', description: 'Manages both clinics' },
    { value: 'clinic_incharge_both', label: 'Clinic In-Charge (Both)', description: 'Manages Forum & Ngee Ann' },
    { value: 'clinic_incharge_forum', label: 'Clinic In-Charge (Forum)', description: 'Manages Forum clinic only' },
    { value: 'clinic_incharge_ngeeann', label: 'Clinic In-Charge (Ngee Ann)', description: 'Manages Ngee Ann clinic only' },
    { value: 'office_admin', label: 'Office Admin', description: 'Office administration, sees all tasks' },
    { value: 'office_user', label: 'Office User', description: 'Office staff, sees own tasks' }
  ];

  const clinics = [
    { value: 'all', label: 'All Clinics' },
    { value: 'forum', label: 'Forum' },
    { value: 'ngeeann', label: 'Ngee Ann' }
  ];

  useEffect(() => {
    fetchUsers();
    fetchAllStaff();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStaff = async () => {
    try {
      const data = await userService.getAllStaff();
      setAllStaff(data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'office_user',
      allowed_clinics: [],
      staff_id: undefined
    });
    setDialogOpen(true);
  };

  const handleEditUser = (user: UserType) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      allowed_clinics: user.allowed_clinics,
      staff_id: user.staff_id
    });
    setDialogOpen(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    
    try {
      if (editingUser) {
        // Update existing user
        const updates: UpdateUserData = {
          name: formData.name,
          role: formData.role,
          allowed_clinics: formData.allowed_clinics,
          staff_id: formData.staff_id || null
        };
        
        if (formData.password) {
          updates.password = formData.password;
        }
        
        const result = await userService.updateUser(editingUser.id, updates);
        
        if (result.success) {
          toast({
            title: 'Success',
            description: 'User updated successfully'
          });
          // Refresh the user list
          await fetchUsers();
          setDialogOpen(false);
        } else {
          toast({
            title: 'Error',
            description: result.error || 'Failed to update user',
            variant: 'destructive'
          });
        }
      } else {
        // Create new user
        const result = await userService.createUser(formData);
        
        if (result.success) {
          toast({
            title: 'Success',
            description: 'User created successfully'
          });
          fetchUsers();
          setDialogOpen(false);
        } else {
          toast({
            title: 'Error',
            description: result.error || 'Failed to create user',
            variant: 'destructive'
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserStatus = async (user: UserType) => {
    try {
      const result = user.is_active
        ? await userService.deactivateUser(user.id)
        : await userService.activateUser(user.id);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: `User ${user.is_active ? 'deactivated' : 'activated'} successfully`
        });
        fetchUsers();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update user status',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive'
      });
    }
  };

  const handleClinicToggle = (clinic: string) => {
    if (clinic === 'all') {
      setFormData({ ...formData, allowed_clinics: ['all'] });
    } else {
      const clinics = formData.allowed_clinics.filter(c => c !== 'all');
      if (clinics.includes(clinic)) {
        setFormData({ ...formData, allowed_clinics: clinics.filter(c => c !== clinic) });
      } else {
        setFormData({ ...formData, allowed_clinics: [...clinics, clinic] });
      }
    }
  };

  // Filter users based on search term and status
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Apply search filter
      const matchesSearch = searchTerm === '' ||
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());

      // Apply status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);

      return matchesSearch && matchesStatus;
    });
  }, [users, searchTerm, statusFilter]);

  if (!canManageUsers()) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You don't have permission to manage users.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>Manage system users and their permissions</CardDescription>
          </div>
          <Button onClick={handleCreateUser} size="sm">
            <Plus className="mr-1 sm:mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Linked Staff</TableHead>
                <TableHead>Clinics</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {roles.find(r => r.value === user.role)?.label || user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.staff_name ? (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span className="text-sm">{user.staff_name}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Not linked</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.allowed_clinics.includes('all') ? (
                      <Badge>All Clinics</Badge>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {user.allowed_clinics.map(clinic => (
                          <Badge key={clinic} variant="secondary">
                            {clinics.find(c => c.value === clinic)?.label || clinic}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={user.is_active}
                      onCheckedChange={() => handleToggleUserStatus(user)}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditUser(user)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user information and permissions' : 'Add a new user to the system'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editingUser}
                placeholder="user@twincityendo.com"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full Name"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="password">
                Password {editingUser && '(leave blank to keep current)'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => {
                  const newRole = value;
                  let newClinics = formData.allowed_clinics;
                  
                  // Auto-set clinic access based on role
                  switch (newRole) {
                    case 'admin':
                      newClinics = ['all'];
                      break;
                    case 'operations_manager':
                    case 'clinic_incharge_both':
                      newClinics = ['forum', 'ngeeann'];
                      break;
                    case 'clinic_incharge_forum':
                      newClinics = ['forum'];
                      break;
                    case 'clinic_incharge_ngeeann':
                      newClinics = ['ngeeann'];
                      break;
                    case 'office_admin':
                    case 'office_user':
                      // Keep existing clinics for office users
                      break;
                  }
                  
                  setFormData({ ...formData, role: newRole as typeof formData.role, allowed_clinics: newClinics });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <div>{role.label}</div>
                        <div className="text-xs text-muted-foreground">{role.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="staff">Link to Staff Member</Label>
              <Select
                value={formData.staff_id || 'none'}
                onValueChange={(value) => {
                  setFormData({ 
                    ...formData, 
                    staff_id: value === 'none' ? undefined : value 
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a staff member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">No staff member linked</span>
                  </SelectItem>
                  {allStaff
                    .filter(staff => 
                      // Show staff that are not linked to any user, or linked to current user
                      !staff.user_id || staff.user_id === editingUser?.id
                    )
                    .map(staff => (
                      <SelectItem key={staff.id} value={staff.id}>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3" />
                          <span>{staff.name}</span>
                          <span className="text-xs text-muted-foreground">({staff.role})</span>
                        </div>
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Link this user to a staff member to allow them to see tasks assigned to that staff
              </p>
            </div>
            
            <div className="grid gap-2">
              <Label>Clinic Access</Label>
              <div className="space-y-2">
                {clinics.map(clinic => (
                  <div key={clinic.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={clinic.value}
                      checked={formData.allowed_clinics.includes(clinic.value)}
                      onCheckedChange={() => handleClinicToggle(clinic.value)}
                    />
                    <Label htmlFor={clinic.value} className="cursor-pointer">
                      {clinic.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveUser} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UserManagement;